import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import {
    apiFetchDeliveryGuyLocation,
    apiFetchAvailableDeliveryGuys,
} from "../service/api/orders";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const POLL_MS = 10_000;

const isValid = (lat, lng) =>
    lat != null && lng != null && !isNaN(Number(lat)) && !isNaN(Number(lng));
const n = (v) => Number(v);

const parseLocation = (str) => {
    if (!str) return null;
    try { return typeof str === "string" ? JSON.parse(str) : str; }
    catch { return null; }
};

const makeScooterIcon = (heading, online, color = null) => {
    const fill = color ?? (online ? "#10b981" : "#94a3b8");

    const rawDeg = Number(heading);
    const deg = Number.isFinite(rawDeg) && rawDeg !== -1 ? rawDeg : 0;

    const html = `
    <div style="width:42px;height:52px;">
      <svg viewBox="0 0 42 52" width="42" height="52">
        
        <!-- Pin shape -->
        <path d="M21 2C12.16 2 5 9.16 5 18c0 11.5 16 31 16 31S37 29.5 37 18C37 9.16 29.84 2 21 2z"
              fill="${fill}" />

        <!-- Inner circle -->
        <circle cx="21" cy="17" r="11" fill="white" />

        <!-- Rotating scooter -->
        <text x="21" y="21"
              text-anchor="middle"
              font-size="14"
              transform="rotate(${deg}, 21, 17)">
          🛵
        </text>

      </svg>
    </div>
  `;

    return L.divIcon({
        html,
        className: "",
        iconSize: [42, 52],
        iconAnchor: [21, 50],   // tip of the pin
        popupAnchor: [0, -50],
    });
};

const svgPin = (svg, size = [36, 44]) =>
    L.divIcon({
        html: svg,
        className: "",
        iconSize: size,
        iconAnchor: [size[0] / 2, size[1]],
        popupAnchor: [0, -size[1] + 4],
    });

const ICONS = {
    restaurant: svgPin(`<svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44"><filter id="dsr"><feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.2"/></filter><ellipse cx="18" cy="41" rx="6" ry="2.5" fill="rgba(0,0,0,0.12)"/><path d="M18 2C10.3 2 4 8.3 4 16c0 10.5 14 26 14 26S32 26.5 32 16C32 8.3 25.7 2 18 2z" fill="#f59e0b" filter="url(#dsr)"/><circle cx="18" cy="16" r="7.5" fill="white"/><path d="M15 11v4a2 2 0 004 0v-4M17 11v10M20 11v3" stroke="#f59e0b" stroke-width="1.6" stroke-linecap="round" fill="none"/></svg>`),
    customer:   svgPin(`<svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44"><filter id="dsc"><feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.2"/></filter><ellipse cx="18" cy="41" rx="6" ry="2.5" fill="rgba(0,0,0,0.12)"/><path d="M18 2C10.3 2 4 8.3 4 16c0 10.5 14 26 14 26S32 26.5 32 16C32 8.3 25.7 2 18 2z" fill="#6366f1" filter="url(#dsc)"/><circle cx="18" cy="16" r="7.5" fill="white"/><circle cx="18" cy="14" r="3" fill="#6366f1"/><path d="M11 22c0-3.5 3.1-5 7-5s7 1.5 7 5" fill="#6366f1"/></svg>`),
};

function FitBounds({ points }) {
    const map = useMap();
    useEffect(() => {
        if (points.length >= 2) {
            map.fitBounds(L.latLngBounds(points), { padding: [48, 48], maxZoom: 15 });
        } else if (points.length === 1) {
            map.setView(points[0], 15);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(points)]);
    return null;
}

function InvalidateSize() {
    const map = useMap();
    useEffect(() => { setTimeout(() => map.invalidateSize(), 80); }, [map]);
    return null;
}

const popupBase = { fontFamily: "'Inter', sans-serif", minWidth: 160 };

const StatusBadge = ({ online }) => (
    <span style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 600,
        background: online ? "#ecfdf5" : "#fef2f2",
        color: online ? "#059669" : "#ef4444",
    }}>
        <span style={{ width: 5, height: 5, borderRadius: "50%", background: online ? "#10b981" : "#ef4444", display: "inline-block" }} />
        {online ? "Online" : "Offline"}
    </span>
);

const DeliveryPopup = ({ guy }) => {
    const heading = Number(guy.heading);
    const hasHeading = !isNaN(heading) && heading !== -1;
    return (
        <div style={popupBase}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#1a1a1a", marginBottom: 5 }}>
                🛵 {guy.name ?? "Delivery Guy"}
            </div>
            {guy.phone && (
                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 3 }}>📞 {guy.phone}</div>
            )}
            {guy.vehicle && (
                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 3 }}>🚗 {guy.vehicle}</div>
            )}
            {hasHeading && (
                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 5 }}>
                    🧭 {Math.round(heading)}° heading
                </div>
            )}
            {guy.non_complete_orders != null && (
                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 5 }}>
                    📦 {guy.non_complete_orders} active · {guy.completed_orders ?? 0} done today
                </div>
            )}
            <StatusBadge online={guy.status === 1} />
        </div>
    );
};

const RestaurantPopup = ({ restaurant }) => (
    <div style={popupBase}>
        <div style={{ fontWeight: 700, fontSize: 13, color: "#1a1a1a", marginBottom: 4 }}>
            🍽 {restaurant?.name}
        </div>
        {restaurant?.address && (
            <div style={{ fontSize: 11, color: "#64748b" }}>{restaurant.address}</div>
        )}
    </div>
);

const CustomerPopup = ({ user, location }) => (
    <div style={popupBase}>
        <div style={{ fontWeight: 700, fontSize: 13, color: "#1a1a1a", marginBottom: 4 }}>
            📍 {user?.name ?? "Customer"}
        </div>
        {location?.house && (
            <div style={{ fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 2 }}>
                {location.house}
            </div>
        )}
        {location?.address && (
            <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>{location.address}</div>
        )}
        {user?.phone && (
            <div style={{ fontSize: 11, color: "#64748b" }}>📞 {user.phone}</div>
        )}
    </div>
);

const Legend = () => (
    <div style={{
        display: "flex", gap: 14, padding: "8px 14px",
        background: "#fff", borderTop: "1px solid #e2e8f0", flexWrap: "wrap",
    }}>
        {[
            { color: "#10b981", label: "Online Rider" },
            { color: "#94a3b8", label: "Offline Rider" },
            { color: "#f59e0b", label: "Restaurant" },
            { color: "#6366f1", label: "Customer" },
        ].map(({ color, label }) => (
            <div key={label} style={{
                display: "flex", alignItems: "center", gap: 6,
                fontSize: 11, fontWeight: 600, color: "#475569",
                fontFamily: "'Inter', sans-serif",
            }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: color, display: "inline-block" }} />
                {label}
            </div>
        ))}
    </div>
);

function HoverMarker({ position, icon, children }) {
    const markerRef = useRef(null);
    return (
        <Marker
            position={position}
            icon={icon}
            ref={markerRef}
            eventHandlers={{
                mouseover: () => markerRef.current?.openPopup(),
                mouseout:  () => markerRef.current?.closePopup(),
            }}
        >
            <Popup autoPan={false} keepInView={false}>
                {children}
            </Popup>
        </Marker>
    );
}

function MapMarkers({ order, deliveryGuys, boundsPoints, invalidate }) {
    const restLat  = n(order?.restaurant?.latitude);
    const restLng  = n(order?.restaurant?.longitude);
    const location = parseLocation(order?.location);
    const custLat  = n(location?.lat);
    const custLng  = n(location?.lng);

    return (
        <>
            <FitBounds points={boundsPoints} />
            {invalidate && <InvalidateSize />}

            {/* Restaurant */}
            {isValid(restLat, restLng) && (
                <HoverMarker position={[restLat, restLng]} icon={ICONS.restaurant}>
                    <RestaurantPopup restaurant={order?.restaurant} />
                </HoverMarker>
            )}

            {/* Customer */}
            {isValid(custLat, custLng) && (
                <HoverMarker position={[custLat, custLng]} icon={ICONS.customer}>
                    <CustomerPopup user={order?.user} location={location} />
                </HoverMarker>
            )}

            {/* Delivery guys — scooter icon rotated by heading */}
            {deliveryGuys.map((guy, idx) => (
                <HoverMarker
                    key={guy.id ?? idx}
                    position={[n(guy.lat), n(guy.lng)]}
                    icon={makeScooterIcon(guy.heading, guy.status === 1)}
                >
                    <DeliveryPopup guy={guy} />
                </HoverMarker>
            ))}
        </>
    );
}

const ExpandIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
        <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
    </svg>
);
const CollapseIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/>
        <line x1="10" y1="14" x2="3" y2="21"/><line x1="21" y1="3" x2="14" y2="10"/>
    </svg>
);

const OverlayBar = ({ isModal, pulsing, acceptDelivery, onRefresh, onToggleExpand }) => (
    <div style={{
        position: "absolute", top: 10, left: 0, right: 0, zIndex: 1000,
        display: "flex", justifyContent: "space-between",
        alignItems: "center", padding: "0 10px", pointerEvents: "none",
    }}>
        <div
            onClick={onRefresh}
            style={{
                pointerEvents: "all", cursor: "pointer", userSelect: "none",
                background: "rgba(255,255,255,0.95)",
                border: `1px solid ${pulsing ? "#f59e0b" : "#e2e8f0"}`,
                borderRadius: 20, padding: "4px 10px",
                fontSize: 11, fontWeight: 600,
                color: pulsing ? "#d97706" : "#64748b",
                fontFamily: "'Inter', sans-serif",
                display: "flex", alignItems: "center", gap: 5,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                transition: "all 0.2s",
            }}
        >
            <span style={{
                width: 6, height: 6, borderRadius: "50%",
                background: pulsing ? "#f59e0b" : "#10b981",
                display: "inline-block",
            }} />
            {pulsing ? "Updating…" : acceptDelivery ? "Tracking rider · 10s" : "All riders · 10s"}
        </div>

        <button
            onClick={onToggleExpand}
            style={{
                pointerEvents: "all", cursor: "pointer",
                background: "rgba(255,255,255,0.95)",
                border: "1px solid #e2e8f0",
                borderRadius: 20, padding: "4px 10px",
                fontSize: 11, fontWeight: 600, color: "#475569",
                fontFamily: "'Inter', sans-serif",
                display: "flex", alignItems: "center", gap: 5,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
        >
            {isModal ? <><CollapseIcon /> Collapse</> : <><ExpandIcon /> Expand</>}
        </button>
    </div>
);

export default function DeliveryMap({ order }) {
    const [deliveryGuys, setDeliveryGuys] = useState([]);
    const [pulsing,      setPulsing]      = useState(false);
    const [error,        setError]        = useState(null);
    const [expanded,     setExpanded]     = useState(false);
    const pollerRef = useRef(null);

    const acceptDelivery = order?.accept_delivery;

    const restLat  = n(order?.restaurant?.latitude);
    const restLng  = n(order?.restaurant?.longitude);
    const location = parseLocation(order?.location);
    const custLat  = n(location?.lat);
    const custLng  = n(location?.lng);

    const centerLat = isValid(restLat, restLng) ? restLat : (isValid(custLat, custLng) ? custLat : 20.5937);
    const centerLng = isValid(restLat, restLng) ? restLng : (isValid(custLat, custLng) ? custLng : 78.9629);

    const boundsPoints = [];
    if (isValid(restLat, restLng)) boundsPoints.push([restLat, restLng]);
    if (isValid(custLat, custLng)) boundsPoints.push([custLat, custLng]);

    const fetchPositions = useCallback(async () => {
        if(![1, 2, 3, 8, 10, 11].includes(order?.orderstatus_id)) return;
        setPulsing(true);
        setError(null);
        try {
            if (acceptDelivery) {
                const userId = acceptDelivery?.user_id ?? acceptDelivery?.user?.id;
                const detail = acceptDelivery?.user?.delivery_guy_detail;

                try {
                    const live = await apiFetchDeliveryGuyLocation(userId);
                    if (live && isValid(live.lat, live.lng)) {
                        setDeliveryGuys([{
                            id:      userId,
                            lat:     live.lat,
                            lng:     live.lng,
                            name:    live.name    ?? acceptDelivery?.user?.name ?? detail?.name,
                            phone:   live.phone   ?? acceptDelivery?.user?.phone,
                            vehicle: live.vehicle_number ?? live.vehicle ?? detail?.vehicle_number,
                            status:  live.status  ?? detail?.status ?? 1,
                            heading: live.heading ?? detail?.heading ?? -1,
                        }]);
                        return;
                    }
                } catch { /* fall through to order data fallback */ }

                // Fallback to order data
                const fallbackLat = detail?.delivery_lat;
                const fallbackLng = detail?.delivery_long;
                if (isValid(fallbackLat, fallbackLng)) {
                    setDeliveryGuys([{
                        id:      userId,
                        lat:     fallbackLat,
                        lng:     fallbackLng,
                        name:    acceptDelivery?.user?.name ?? detail?.name,
                        phone:   acceptDelivery?.user?.phone,
                        vehicle: detail?.vehicle_number,
                        status:  detail?.status ?? 1,
                        heading: detail?.heading ?? -1,
                    }]);
                } else {
                    setDeliveryGuys([]);
                }
            } else {
                const guys = await apiFetchAvailableDeliveryGuys();
                setDeliveryGuys(
                    (guys ?? [])
                        .filter((g) => isValid(g.lat, g.lng))
                        .map((g) => ({
                            id:                  g.id,
                            lat:                 g.lat,
                            lng:                 g.lng,
                            name:                g.name,
                            phone:               g.phone,
                            vehicle:             g.vehicle_number ?? g.vehicle,
                            status:              g.status,
                            heading:             g.heading ?? -1,
                            non_complete_orders: g.non_complete_orders,
                            completed_orders:    g.completed_orders,
                        }))
                );
            }
        } catch (e) {
            setError("Could not fetch delivery locations.");
            console.error(e);
        } finally {
            setPulsing(false);
        }
    }, [acceptDelivery]);

    useEffect(() => {
        if (!order) return;
        fetchPositions();
        pollerRef.current = setInterval(fetchPositions, POLL_MS);
        return () => clearInterval(pollerRef.current);
    }, [fetchPositions, order]);

    useEffect(() => {
        const handler = (e) => { if (e.key === "Escape") setExpanded(false); };
        if (expanded) document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [expanded]);

    const errorBar = error ? (
        <div style={{
            position: "absolute", bottom: 44, left: 0, right: 0, zIndex: 999,
            background: "rgba(239,68,68,0.92)", color: "#fff",
            textAlign: "center", padding: "6px 12px",
            fontSize: 12, fontFamily: "'Inter', sans-serif", fontWeight: 600,
        }}>
            {error} —{" "}
            <span style={{ cursor: "pointer", textDecoration: "underline" }} onClick={fetchPositions}>
                Retry
            </span>
        </div>
    ) : null;

    const inlineMap = (
        <div style={{ position: "relative", borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0" }}>
            <OverlayBar isModal={false} pulsing={pulsing} acceptDelivery={acceptDelivery} onRefresh={fetchPositions} onToggleExpand={() => setExpanded((v) => !v)} />
            <div style={{ height: 300 }}>
                <MapContainer
                    center={[centerLat, centerLng]} zoom={13}
                    style={{ height: "100%", width: "100%" }}
                    attributionControl={false}
                >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <MapMarkers
                        order={order} deliveryGuys={deliveryGuys}
                        boundsPoints={boundsPoints} invalidate={false}
                    />
                </MapContainer>
            </div>
            {errorBar}
            <Legend />
        </div>
    );

    const modalMap = expanded ? createPortal(
        <div
            style={{
                position: "fixed", inset: 0, zIndex: 9999,
                background: "rgba(15,23,42,0.6)",
                backdropFilter: "blur(4px)",
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: 24,
            }}
            onClick={() => setExpanded(false)}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: "min(1100px, calc(100vw - 48px))",
                    height: "min(720px, calc(100vh - 48px))",
                    background: "#fff", borderRadius: 16, overflow: "hidden",
                    boxShadow: "0 24px 64px rgba(0,0,0,0.3)",
                    display: "flex", flexDirection: "column",
                    animation: "dm-slide 0.2s ease",
                }}
            >
                {/* Header */}
                <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "13px 18px", borderBottom: "1px solid #e2e8f0",
                    background: "#fff", flexShrink: 0,
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: "#1a1a1a", fontFamily: "'Inter', sans-serif" }}>
                            Delivery Route
                        </span>
                        {order?.unique_order_id && (
                            <span style={{
                                fontSize: 11, fontWeight: 600, color: "#6366f1",
                                background: "#eef2ff", padding: "2px 8px", borderRadius: 20,
                                fontFamily: "'Inter', sans-serif",
                            }}>
                                {order.unique_order_id}
                            </span>
                        )}
                        {acceptDelivery && (
                            <span style={{
                                fontSize: 11, fontWeight: 600, color: "#059669",
                                background: "#ecfdf5", padding: "2px 8px", borderRadius: 20,
                                fontFamily: "'Inter', sans-serif",
                            }}>
                                🛵 {acceptDelivery?.user?.name ?? acceptDelivery?.user?.delivery_guy_detail?.name}
                            </span>
                        )}
                    </div>
                    <button
                        onClick={() => setExpanded(false)}
                        style={{
                            width: 28, height: 28, borderRadius: "50%",
                            border: "1px solid #e2e8f0", background: "#f1f5f9",
                            color: "#64748b", fontSize: 12, cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                    >✕</button>
                </div>

                {/* Map */}
                <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
                    <OverlayBar isModal={true} pulsing={pulsing} acceptDelivery={acceptDelivery} onRefresh={fetchPositions} onToggleExpand={() => setExpanded((v) => !v)} />
                    <MapContainer
                        center={[centerLat, centerLng]} zoom={13}
                        style={{ height: "100%", width: "100%" }}
                        attributionControl={false}
                    >
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        <MapMarkers
                            order={order} deliveryGuys={deliveryGuys}
                            boundsPoints={boundsPoints} invalidate={true}
                        />
                    </MapContainer>
                    {errorBar}
                </div>

                <Legend />
            </div>
        </div>,
        document.body
    ) : null;

    return (
        <>
            {inlineMap}
            {modalMap}
        </>
    );
}